import React, { useEffect, useState } from "react";
import PartSelect from "../PartSelect";
import ShapeChange from "../ShapeChange";
import DrawingPrepare from "../DrawingPrepare";
import ResumeNotice from "../helpers/exercise/ResumeNotice";
import ExerciseTabs from "../helpers/exercise/ExerciseTabs";
import ExerciseFinish from "../helpers/exercise/ExerciseFinish";
import { useExerciseProgress } from "../helpers/exercise/useExerciseProgress";
import { useExerciseStepLog } from "../helpers/exercise/useExerciseStepLog";
import ExerciseHeading from "../helpers/exercise/ExerciseHeading";
import { preloadImages } from '../../utils/imageCache';
import { getExerciseImageUrls } from '../../config/drawings';

// 問ごとのモデル名マップ（問順の正本）。インデックス0 = 問1。
// 前半はグラス・お皿モチーフ（問1〜4）、後半は立方体モチーフ（問5〜6）。
//
// ★演習で扱うのは6問だけ（2026-10-14週例）★ 残りの誤り（model3・model4・model5・
//   cube_model2・cube_model3・cube_model4）はプレ／ポスト／遅延テストの転移課題として使う
//   （演習で同じ問題を練習した効果・覚えていた効果と区別するため）。選び方の根拠は
//   research/20261019_演習で扱う問題.docx（原因が同じ誤りの組は片方だけ入れる、等）。
//   2026-10-16に問4を model5 → model8 に差し替えた（model5 は真下から見ないとおかしさが
//   出ず分かりにくいため。model8 は横から見ると皿の底が沈みこむのがはっきり見える）。
//   2026-10-19に問6を cube_model3 → cube_model5 に差し替えた（視点変更の問2と同じ誤りにそろえ、
//   立方体でも「同じ誤りを形と視点の両方から説明する」問題を1問置く。理由は Ex1_2026_Gaze.js）。
//   候補語彙（selfExpOptions.js）・視点の定義・デッサン画像は全モデルぶん残してある。
// ★model7（グラスを斜めに描く誤り）は演習では扱わない（2026-10-03決定）★
//   カテゴリスキーマに起因する誤りとして説明できないため。テストには出題する。
export const SHAPE_MODEL_MAP = [
    'model1',      // 問1: 皿を丸く描きすぎる誤り（横から見ると空中で傾斜して顕在化）
    'model2',      // 問2: 皿を大きく描きすぎる誤り（全体スケール）
    'model6',      // 問3: 皿のふち（リム）を広く描きすぎる誤り
    'model8',      // 問4: 皿を深く描きすぎる誤り（横から見ると皿の底がグラスの底より下に沈みこむ）
    'cube_model1', // 問5: 幅の発散遠近（上面の後退辺が奥へ開く）
    'cube_model5', // 問6: 上面を縦に広く描く誤り（横から見ると側面が歪んだ四角形に見える）
];

export default function Ex_1_2026_Shape() {
    const modelMap = SHAPE_MODEL_MAP;
    const QUESTIONS = modelMap.length; // 問数はモデルマップの長さに合わせる
    const STEPS_PER_QUESTION = 2; // 各問あたりのステップ数: 0=選択,1=表示
    const TOTAL_STEPS = QUESTIONS * STEPS_PER_QUESTION;
    const getGlbForQuestion = (qIdx) => modelMap[qIdx] || 'model1';

    // 進行状態は番号ごとに保存され、リロード・中断後も続きから再開できる
    const {
        index, questionIndex, isFinished, selectedParts, extra,
        isResumedStep, isStepCompleted, getStepAnswer, canGoToIndex, goToIndex,
        markReached, setSelectedPart, updateExtra, markStepAnswered, completeStep,
    } = useExerciseProgress({
        exerciseKey: 'shape',
        questions: QUESTIONS,
        stepsPerQuestion: STEPS_PER_QUESTION,
    });

    // 各問題ごとの ShapeChange 初期カメラ位置（保存対象なので extra に置く）
    const initialCameraPositions = extra.cameraPositions || {};

    // 画像準備結果を親で保持（再生成できるので保存はしない）
    const [drawingImages, setDrawingImages] = useState(() => Array(QUESTIONS).fill([null, null, null]));
    const [imagesLoading, setImagesLoading] = useState(false);

    // 問×ステップを開くたびに、ログの文脈・所要時間の計測・表示の記録を切り替える
    useExerciseStepLog({ exercise: 'shape', index, stepsPerQuestion: STEPS_PER_QUESTION, modelMap, active: !isFinished });
    // 全問の画像を先に読み込んでおく（途中で通信が切れても次の問の画像が出るように）
    useEffect(() => { preloadImages(getExerciseImageUrls(modelMap, { mid: true })); }, [modelMap]);

    // answerDetail＝自己説明の回答内容（プルダウンの選択・記述）。戻ったときの再表示に使う
    const handleComplete = (selectedPartFromChild, cameraFromChild, selfText, answerDetail) => {
        // 子コンポーネントが渡してくれたカメラ位置を保存
        if (cameraFromChild) {
            updateExtra(prev => ({
                ...prev,
                cameraPositions: { ...(prev.cameraPositions || {}), [questionIndex]: cameraFromChild },
            }));
        }
        // 選択部品・回答内容の保存と次ステップへの前進（最後なら完了）はフックが担う
        completeStep(selectedPartFromChild, answerDetail);
    };

    const onImagesReadyForQuestion = (images, qIdx) => {
        // images は配列（3枚）の想定
        setDrawingImages(prev => {
            const copy = [...prev];
            copy[qIdx] = images;
            return copy;
        });
        setImagesLoading(false);
        markReached(index);
    };

    // prepareKey を使って必要なときだけ DrawingPrepare をマウントして準備する
    const [prepareKey, setPrepareKey] = useState(null);

    // questionIndex が変わったら、その問の画像が未準備なら準備を開始する
    useEffect(() => {
        const q = questionIndex;
        const imagesForQ = drawingImages[q];
        const needPrepare = !imagesForQ || imagesForQ.every(i => i == null);
        if (needPrepare) {
            setImagesLoading(true);
            // key に問番号を使って DrawingPrepare を再マウントさせる
            setPrepareKey(`prepare-${q}-${Date.now()}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionIndex]);

    const renderContentForIndex = (idx) => {
        const qIdx = Math.floor(idx / STEPS_PER_QUESTION);
        const stepWithin = idx % STEPS_PER_QUESTION;

        if (stepWithin === 0) {
            // 0: 選択系コンポーネント
            // 画像準備が終わっていなければローディング表示を出す
            const imagesReady = drawingImages[qIdx] && !drawingImages[qIdx].every(i => i == null);
            if (!imagesReady || imagesLoading) {
                return (
                    <div className="w-full h-48 flex items-center justify-center">
                        <p>読み込み中...</p>
                    </div>
                );
            }

            return (
                <PartSelect
                    key={`part-select-${qIdx}`}
                    number={qIdx + 1}
                    glbPath={getGlbForQuestion(qIdx)}
                    setSelectedPart={(val) => setSelectedPart(qIdx, val)}
                    // 回答済みの問へ戻ったときは当時の着色を復元し、選び直しと【次に進む】を止める
                    initialSelectedPart={selectedParts[qIdx]}
                    locked={isStepCompleted(idx)}
                    onComplete={(selected, camera, selfText, detail) => handleComplete(selected, camera, selfText, detail)}
                />
            );
        }

        // 1: 表示系コンポーネント（選択した部品は説明シート側に表示するのでここでは出さない）
        return (
            <div>
                <ShapeChange
                    key={`shape-change-${qIdx}`}
                    number={qIdx + 1}
                    selectedPart={selectedParts[qIdx]}
                    errorGlbPath={getGlbForQuestion(qIdx)}
                    drawingImages={drawingImages[qIdx]}
                    imagesLoading={!drawingImages[qIdx] || drawingImages[qIdx].every(i => i == null)}
                    initialCameraPosition={initialCameraPositions[qIdx] || null}
                    isLastQuestion={idx >= TOTAL_STEPS - 1}
                    // 回答済みの問へ戻ったときは当時の回答を復元し、【決定】を止める
                    answer={getStepAnswer(idx)}
                    locked={isStepCompleted(idx)}
                    // 【決定】の時点で回答済みにする（「次の問題に進みます」の間にリロードしても答え直しにしない）
                    onAnswered={(detail) => markStepAnswered(selectedParts[qIdx], detail)}
                    onComplete={(selected, camera, selfText, detail) => handleComplete(selected, camera, selfText, detail)}
                />
            </div>
        );
    };

    if (isFinished) {
        return <ExerciseFinish exerciseName="形状変更演習" />;
    }

    return (
        <div className="page">
            {/* 非表示で画像準備コンポーネントをマウント（prepareKey がセットされているとき） */}
            {prepareKey && (
                <div style={{ visibility: 'hidden', position: 'absolute', left: '-10000px' }}>
                    <DrawingPrepare
                        key={prepareKey}
                        errorGlbPath={getGlbForQuestion(questionIndex)}
                        onImagesReady={(images) => onImagesReadyForQuestion(images, questionIndex)}
                    />
                </div>
            )}
            {/* 学習者向け見出し（内部のバージョン名・英語名は出さない） */}
            <ExerciseHeading title="形状変更演習" />

            <ResumeNotice questionNumber={isResumedStep ? questionIndex + 1 : null} />

            <ExerciseTabs
                totalSteps={TOTAL_STEPS}
                stepsPerQuestion={STEPS_PER_QUESTION}
                index={index}
                canGoToIndex={canGoToIndex}
                isStepCompleted={isStepCompleted}
                onSelect={goToIndex}
            />

            <div className="content">{renderContentForIndex(index)}</div>
        </div>
    );
}
